import React, { useEffect, useMemo, useState } from 'react';
import {
  CosmeticRarity,
  CosmeticType,
  type CosmeticCatalogEntry,
  type PlayerLoadout,
  type PublicPlayerAppearance,
  type UserLoadoutDTO,
} from '@push.io/shared';
import { useAccountController, useAccountSnapshot } from '../../account/context.js';
import { startGoogleSignIn } from '../../account/google-sign-in.js';
import { SLOT_BY_TYPE } from '../../account/slot-map.js';
import type { CosmeticSlotKey } from '../../account/types.js';

import { PURCHASE_ERROR_MESSAGES, type PurchaseErrorCode } from '../../account/account-controller.js';
import type { MenuLanguage } from '../main-menu/types.js';
import { WalletDisplay } from './WalletDisplay.js';
import { PlayerPreview } from './PlayerPreview.js';
import { CoinIcon } from './CoinIcon.js';
import './customization.css';

const CATEGORY_ORDER: CosmeticType[] = [
  CosmeticType.BaseColor,
  CosmeticType.Pattern,
  CosmeticType.Flag,
  CosmeticType.Accessory,
  CosmeticType.Letter,
  CosmeticType.Badge,
  CosmeticType.Nameplate,
  CosmeticType.VictoryEffect,
  CosmeticType.Skin,
];

const HIDDEN_IF_EMPTY = new Set<CosmeticType>([
  CosmeticType.Letter,
  CosmeticType.Badge,
  CosmeticType.Nameplate,
  CosmeticType.VictoryEffect,
  CosmeticType.Skin,
]);

const DEFAULT_PREVIEW: PlayerLoadout = {
  baseColorId: 'cosm_base_blue',
  skinId: null,
  patternId: null,
  flagId: null,
  letterId: null,
  accessoryId: null,
  trailId: null,
  nameplateId: null,
  badgeId: null,
  victoryEffectId: null,
};

export function CustomizationScreen({ language, onBack }: {
  language: MenuLanguage;
  onBack: () => void;
}) {
  const account = useAccountController();
  const snapshot = useAccountSnapshot();
  const [category, setCategory] = useState<CosmeticType>(CosmeticType.BaseColor);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [status, setStatus] = useState<{ tone: 'idle' | 'error' | 'success'; text: string }>({ tone: 'idle', text: '' });
  const [purchasing, setPurchasing] = useState(false);
  const [confirmBuy, setConfirmBuy] = useState<CosmeticCatalogEntry | null>(null);
  const [accessoryColorDraft, setAccessoryColorDraft] = useState<string | null>(null);

  const tr = (es: string, en: string, fr: string) =>
    language === 'es' ? es : language === 'fr' ? fr : en;

  useEffect(() => {
    void account.refreshAll();
  }, [account]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (confirmBuy) { setConfirmBuy(null); return; }
        onBack();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onBack, confirmBuy]);

  const categories = useMemo(() => CATEGORY_ORDER.filter((type) => {
    const catalogItems = snapshot.catalog.filter((item) => item.type === type);
    return catalogItems.length > 0 || !HIDDEN_IF_EMPTY.has(type);
  }), [snapshot.catalog]);

  useEffect(() => {
    if (!categories.includes(category) && categories.length > 0) {
      setCategory(categories[0]);
    }
  }, [categories, category]);

  const items = snapshot.catalog.filter((item) => item.type === category);
  const equippedId = snapshot.loadout && SLOT_BY_TYPE[category]
    ? snapshot.loadout[SLOT_BY_TYPE[category] as CosmeticSlotKey]
    : null;
  const selectedItem = items.find((item) => item.id === selectedId)
    ?? items.find((item) => item.id === equippedId)
    ?? items[0]
    ?? null;
  const selectedSlot = selectedItem ? SLOT_BY_TYPE[selectedItem.type] ?? null : null;
  const effectiveAccessoryColor = accessoryColorDraft ?? snapshot.loadout?.accessoryColor ?? null;
  const previewAppearance = mergePreviewAppearance(normalizePreviewAppearance(snapshot.loadout), selectedItem);
  if (effectiveAccessoryColor) {
    previewAppearance.accessoryColor = effectiveAccessoryColor;
  }
  const saving = selectedSlot ? snapshot.savingSlots.includes(selectedSlot) : false;
  const isGuest = snapshot.account?.accountType !== 'REGISTERED';

  const owned = selectedItem ? account.isOwned(selectedItem.id) : false;
  const balance = snapshot.wallet?.softCurrency ?? 0;
  const canAfford = selectedItem ? (selectedItem.priceSoftCurrency != null && balance >= selectedItem.priceSoftCurrency) : false;

  const handleEquip = async () => {
    if (!selectedItem || !selectedSlot || !owned) return;
    try {
      await account.equip({
        requestId: `equip_${selectedItem.id}_${Date.now()}`,
        cosmeticId: selectedItem.id,
        slot: selectedSlot,
      });
      setStatus({ tone: 'success', text: tr('Guardado', 'Saved', 'Enregistré') });
    } catch {
      setStatus({ tone: 'error', text: tr('Error al guardar', 'Save failed', "Échec de l'enregistrement") });
    }
  };

  const handleUnequip = async () => {
    if (!selectedSlot || !account.canUnequip(selectedSlot)) return;
    try {
      await account.unequip({
        requestId: `unequip_${selectedSlot}_${Date.now()}`,
        slot: selectedSlot,
      });
      setStatus({ tone: 'success', text: tr('Guardado', 'Saved', 'Enregistré') });
    } catch {
      setStatus({ tone: 'error', text: tr('Error al guardar', 'Save failed', "Échec de l'enregistrement") });
    }
  };

  const handleBuyClick = () => {
    if (!selectedItem || owned || purchasing) return;
    if (!selectedItem.priceSoftCurrency) return;
    setConfirmBuy(selectedItem);
  };

  const handleGoogleSignIn = async () => {
    try {
      await startGoogleSignIn();
    } catch {
      setStatus({
        tone: 'error',
        text: tr(
          'No se pudo iniciar sesión con Google.',
          'Could not start Google sign-in.',
          'Impossible de démarrer la connexion avec Google.',
        ),
      });
    }
  };

  const handleConfirmBuy = async () => {
    if (!confirmBuy || purchasing) return;
    setPurchasing(true);
    setConfirmBuy(null);
    try {
      await account.purchase(confirmBuy.id);
      setStatus({
        tone: 'success',
        text: tr('¡Comprado!', 'Purchased!', 'Acheté !'),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const known: PurchaseErrorCode =
        msg.includes('insufficient_funds') ? 'insufficient_funds'
        : msg.includes('already_owned') ? 'already_owned'
        : msg.includes('not_found') ? 'not_found'
        : 'server_error';
      const labels = PURCHASE_ERROR_MESSAGES[known];
      setStatus({
        tone: 'error',
        text: language === 'es' ? labels.es : language === 'fr' ? labels.fr : labels.en,
      });
    } finally {
      setPurchasing(false);
    }
  };

  const actionButton = () => {
    if (!selectedItem || !selectedSlot) return null;
    if (owned) {
      if (account.isEquipped(selectedItem.id)) {
        return (
          <button className="customization-action" disabled>
            {tr('Equipado', 'Equipped', 'Équipé')}
          </button>
        );
      }
      return (
        <button className="customization-action" disabled={saving} onClick={handleEquip}>
          {saving ? tr('Guardando', 'Saving', 'Enregistrement') : tr('Equipar', 'Equip', 'Équiper')}
        </button>
      );
    }
    const price = selectedItem.priceSoftCurrency;
    if (price == null || price <= 0) return null;
    if (!canAfford) {
      return (
        <button className="customization-action buy" disabled>
          <span className="customization-action-buy">
            <CoinIcon size={14} />
            <span>{tr('Comprar', 'Buy', 'Acheter')} {price}</span>
          </span>
        </button>
      );
    }
    return (
      <button className="customization-action buy" onClick={handleBuyClick} disabled={purchasing}>
        {purchasing ? tr('Comprando…', 'Buying…', 'Achat…') : (
          <span className="customization-action-buy">
            <CoinIcon size={14} />
            <span>{tr('Comprar', 'Buy', 'Acheter')} {price}</span>
          </span>
        )}
      </button>
    );
  };

  const renderDetailsModule = (scope: 'catalog' | 'preview') => (
    <>
      <div className="customization-details-copy">
        <strong>{selectedItem ? localizeName(selectedItem, language) : tr('Nada seleccionado', 'Nothing selected', 'Aucune sélection')}</strong>
        <span>{selectedItem ? formatRarity(selectedItem.rarity, language) : ''}</span>
        <small>{selectedItem ? renderState(selectedItem, account, selectedSlot, language) : ''}</small>
        {selectedItem?.type === CosmeticType.Accessory && (
          <div className="accessory-color-row">
            <label htmlFor={`accessory-color-input-${scope}`}>
              {tr('Color del accesorio', 'Accessory color', 'Couleur de l’accessoire')}
            </label>
            <input
              id={`accessory-color-input-${scope}`}
              type="color"
              value={effectiveAccessoryColor ?? '#ffffff'}
              onInput={(e) => setAccessoryColorDraft((e.target as HTMLInputElement).value)}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;
                if (owned && account.getEquippedCosmeticId('accessoryId')) {
                  setAccessoryColorDraft(null);
                  void account.setAccessoryColor(value).catch(() => undefined);
                } else {
                  setAccessoryColorDraft(value);
                }
              }}
            />
            <span className="accessory-color-hex">
              {(effectiveAccessoryColor ?? '#ffffff').toUpperCase()}
            </span>
          </div>
        )}
      </div>
      {actionButton()}
      {owned && (
        <button
          className="customization-action secondary"
          disabled={!selectedSlot || !account.canUnequip(selectedSlot) || !account.getEquippedCosmeticId(selectedSlot) || saving}
          onClick={handleUnequip}
        >
          {tr('Quitar', 'Remove', 'Retirer')}
        </button>
      )}
    </>
  );

  return (
    <section className={`customization-shell ${isGuest ? 'has-guest-banner' : ''}`}>
      <header className="customization-header">
        <div className="customization-header-left">
          <button className="customization-back" onClick={onBack}>
            {tr('Volver', 'Back', 'Retour')}
          </button>
          <div className="customization-title">
            <strong>{tr('Personalizar', 'Customize', 'Personnaliser')}</strong>
            <span>{tr('Loadout persistente del jugador', 'Persistent player loadout', 'Équipement persistant du joueur')}</span>
          </div>
        </div>
        <WalletDisplay wallet={snapshot.wallet} language={language} />
      </header>

      {isGuest && (
        <aside className="customization-guest-banner" role="note">
          <span className="customization-guest-banner-icon" aria-hidden="true">⚠</span>
          <p>
            {tr(
              'Estás jugando como Invitado. Inicia sesión para guardar de forma permanente tus skins y mejoras.',
              'You are playing as a Guest. Sign in to permanently save your skins and upgrades.',
              'Vous jouez en tant qu’Invité. Connectez-vous pour sauvegarder définitivement vos skins et améliorations.',
            )}
          </p>
          <button onClick={() => void handleGoogleSignIn()}>
            {tr('Iniciar sesión con Google', 'Sign in with Google', 'Se connecter avec Google')}
          </button>
        </aside>
      )}

      <div className="customization-body">
        <aside className="customization-preview-panel">
          <div className="customization-preview-stage">
            <PlayerPreview appearance={previewAppearance} playerName="Jugador" reducedMotion={false} />
          </div>
          <div className="customization-preview-meta">
            <strong>{selectedItem ? localizeName(selectedItem, language) : tr('Sin selección', 'No selection', 'Aucune sélection')}</strong>
            <span>{selectedItem ? formatType(selectedItem.type, language) : ''}</span>
            <p>{selectedItem ? localizeDescription(selectedItem, language) : tr('Selecciona un cosmético para ver y guardar el cambio.', 'Select a cosmetic to preview and save the change.', 'Sélectionnez un cosmétique pour prévisualiser et enregistrer le changement.')}</p>
          </div>
          <div className="customization-details customization-details-mobile">
            {renderDetailsModule('preview')}
          </div>
          <div className={`customization-status ${status.tone === 'error' ? 'error' : status.tone === 'success' ? 'success' : ''}`}>
            {snapshot.loading
              ? tr('Cargando catálogo…', 'Loading catalog…', 'Chargement du catalogue…')
              : snapshot.error
                ? tr('Error de cuenta: ', 'Account error: ', 'Erreur de compte : ') + snapshot.error
                : status.text}
          </div>
          </aside>

        <section className="customization-catalog-panel">
          <nav className="customization-tabs" aria-label={tr('Categorías de cosméticos', 'Cosmetic categories', 'Catégories de cosmétiques')}>
            {categories.map((item) => (
              <button
                key={item}
                className={item === category ? 'active' : ''}
                onClick={() => {
                  setCategory(item);
                  setSelectedId(null);
                }}
              >
                {formatType(item, language)}
              </button>
            ))}
          </nav>

          <div className="customization-details customization-details-catalog">
            {renderDetailsModule('catalog')}
          </div>

          <div className="customization-grid" role="list">
            {items.map((item) => {
              const isOwned = account.isOwned(item.id);
              const equipped = account.isEquipped(item.id);
              const selected = selectedItem?.id === item.id;
              const price = item.priceSoftCurrency;
              const affordable = price != null && balance >= price;
              const isPrice = price != null && price > 0;
              const stateText = equipped
                ? tr('Equipado', 'Equipped', 'Équipé')
                : isOwned
                  ? tr('Obtenido', 'Obtained', 'Obtenu')
                  : isPrice
                    ? `${price}`
                    : item.isDefault
                      ? tr('Gratis', 'Free', 'Gratuit')
                      : tr('Bloqueado', 'Locked', 'Bloqué');
              return (
                <button
                  key={item.id}
                  role="listitem"
                  className={`cosmetic-card ${selected ? 'selected' : ''} ${equipped ? 'equipped' : ''} ${isOwned ? '' : 'locked'} ${!isOwned && price != null && price > 0 && !affordable ? 'funds-insufficient' : ''}`}
                  onClick={() => setSelectedId(item.id)}
                  aria-label={`${localizeName(item, language)}. ${formatType(item.type, language)}. ${formatRarity(item.rarity, language)}. ${stateText}.`}
                >
                  <div className={`cosmetic-card-preview cosmetic-card-preview-${item.type}`} aria-hidden="true">
                    {renderCardPreview(item, effectiveAccessoryColor)}
                  </div>
                  <div className="cosmetic-card-title">{localizeName(item, language)}</div>
                  <span className="cosmetic-card-meta">{formatRarity(item.rarity, language)}</span>
                  <span className={`cosmetic-card-state ${!isOwned && isPrice ? 'price' : ''} ${!isOwned && isPrice && !affordable ? 'price-insufficient' : ''}`}>
                    {!isOwned && isPrice ? (
                      <span className="cosmetic-card-state-price">
                        <CoinIcon size={10} />
                        {price}
                      </span>
                    ) : stateText}
                  </span>
                </button>
              );
            })}
            {items.length === 0 && (
              <div className="customization-status">
                {tr('No hay cosméticos en esta categoría.', 'There are no cosmetics in this category.', 'Aucun cosmétique dans cette catégorie.')}
              </div>
            )}
          </div>
        </section>
      </div>

      {confirmBuy && (
        <div className="purchase-overlay" onClick={() => setConfirmBuy(null)}>
          <div className="purchase-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>
              {isGuest
                ? tr('¿Comprar como invitado?', 'Buy as a guest?', 'Acheter en tant qu’invité ?')
                : `${tr('Comprar', 'Buy', 'Acheter')} "${localizeName(confirmBuy, language)}"`}
            </h2>
            {isGuest && (
              <p className="purchase-dialog-warning">
                {tr(
                  'Tu progreso aún no está vinculado a una cuenta. Inicia sesión para recuperar esta compra en otro dispositivo.',
                  'Your progress is not yet linked to an account. Sign in to recover this purchase on another device.',
                  'Votre progression n’est pas encore liée à un compte. Connectez-vous pour récupérer cet achat sur un autre appareil.',
                )}
              </p>
            )}
<div className="purchase-dialog-price">
              <span>{tr('Precio:', 'Price:', 'Prix :')}</span>
              <CoinIcon size={14} />
              <strong>{confirmBuy.priceSoftCurrency}</strong>
            </div>
            {isGuest ? (
              <>
                <div className="purchase-dialog-actions guest-purchase-actions">
                  <button className="purchase-dialog-signin" onClick={() => void handleGoogleSignIn()}>
                    {tr('Iniciar sesión con Google', 'Sign in with Google', 'Se connecter avec Google')}
                  </button>
                  <button className="purchase-dialog-continue" onClick={handleConfirmBuy}>
                    {tr('Comprar de todos modos', 'Buy anyway', 'Acheter quand même')}
                  </button>
                </div>
                <button className="purchase-dialog-dismiss" onClick={() => setConfirmBuy(null)}>
                  {tr('Cancelar', 'Cancel', 'Annuler')}
                </button>
              </>
            ) : (
              <div className="purchase-dialog-actions">
                <button className="purchase-dialog-cancel" onClick={() => setConfirmBuy(null)}>
                  {tr('Cancelar', 'Cancel', 'Annuler')}
                </button>
                <button className="purchase-dialog-confirm" onClick={handleConfirmBuy}>
                  {tr('Comprar', 'Buy', 'Acheter')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function normalizePreviewAppearance(loadout: PlayerLoadout | UserLoadoutDTO | null | undefined): PublicPlayerAppearance {
  return {
    baseColorId: loadout?.baseColorId || DEFAULT_PREVIEW.baseColorId,
    skinId: loadout?.skinId ?? null,
    patternId: loadout?.patternId ?? null,
    flagId: loadout?.flagId ?? null,
    letterId: loadout?.letterId ?? null,
    accessoryId: loadout?.accessoryId ?? null,
    trailId: loadout?.trailId ?? DEFAULT_PREVIEW.trailId,
    nameplateId: loadout?.nameplateId ?? null,
    badgeId: loadout?.badgeId ?? null,
    victoryEffectId: loadout?.victoryEffectId ?? null,
  };
}

function mergePreviewAppearance(loadout: PublicPlayerAppearance, selectedItem: CosmeticCatalogEntry | null): PublicPlayerAppearance {
  if (!selectedItem) return loadout;
  const next = { ...loadout };
  switch (selectedItem.type) {
    case CosmeticType.BaseColor:
      next.baseColorId = selectedItem.id;
      next.flagId = null;
      next.patternId = null;
      break;
    case CosmeticType.Pattern:
      next.patternId = selectedItem.id;
      next.flagId = null;
      break;
    case CosmeticType.Flag:
      next.flagId = selectedItem.id;
      next.patternId = null;
      break;
    case CosmeticType.Accessory:
      next.accessoryId = selectedItem.id;
      break;
    default:
      break;
  }
  return next;
}

function localizeName(item: CosmeticCatalogEntry, language: MenuLanguage): string {
  const metadataName = typeof item.metadata?.label === 'string' ? item.metadata.label : null;
  return metadataName ?? humanizeCosmeticSlug(item.slug, item.type, language);
}

function localizeDescription(item: CosmeticCatalogEntry, language: MenuLanguage): string {
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  switch (item.type) {
    case CosmeticType.BaseColor:
      return tr('Color base persistente para el jugador.', 'Persistent base color for the player.', 'Couleur de base persistante du joueur.');
    case CosmeticType.Pattern:
      return tr('Patrón sutil aplicado sobre el color base.', 'Subtle pattern applied over the base color.', 'Motif subtil appliqué sur la couleur de base.');
    case CosmeticType.Flag:
      return tr('Bandera recortada dentro del jugador.', 'Flag clipped inside the player.', 'Drapeau découpé dans le joueur.');
    case CosmeticType.Accessory:
      return tr('Accesorio visual sin efecto en la hitbox.', 'Visual accessory with no hitbox effect.', 'Accessoire visuel sans effet sur la hitbox.');
    default:
      return humanizeKey(item.descriptionKey);
  }
}

function humanizeKey(key: string): string {
  const value = key.split('.').slice(-2, -1)[0] ?? key.split('.').pop() ?? key;
  return capitalize(value.replace(/[_-]+/g, ' ').trim());
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function humanizeCosmeticSlug(slug: string, _type: CosmeticType, language: MenuLanguage): string {
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  const normalized = slug.replace(/^(base-|pattern-|flag-|accessory-|trail-)/, '');
  switch (normalized) {
    case 'blue': return tr('Azul', 'Blue', 'Bleu');
    case 'red': return tr('Rojo', 'Red', 'Rouge');
    case 'green': return tr('Verde', 'Green', 'Vert');
    case 'yellow': return tr('Amarillo', 'Yellow', 'Jaune');
    case 'purple': return tr('Morado', 'Purple', 'Violet');
    case 'white': return tr('Blanco', 'White', 'Blanc');
    case 'orange': return tr('Naranja', 'Orange', 'Orange');
    case 'pink': return tr('Rosa', 'Pink', 'Rose');
    case 'cyan': return tr('Cian', 'Cyan', 'Cyan');
    case 'lime': return tr('Lima', 'Lime', 'Citron vert');
    case 'coral': return tr('Coral', 'Coral', 'Corail');
    case 'teal': return tr('Verde azulado', 'Teal', 'Sarcelle');
    case 'indigo': return tr('Índigo', 'Indigo', 'Indigo');
    case 'violet': return tr('Violeta', 'Violet', 'Violet');
    case 'gold': return tr('Dorado', 'Gold', 'Or');
    case 'silver': return tr('Plateado', 'Silver', 'Argent');
    case 'stripes': return tr('Rayas', 'Stripes', 'Rayures');
    case 'rings': return tr('Anillos', 'Rings', 'Anneaux');
    case 'dots': return tr('Puntos', 'Dots', 'Pois');
    case 'waves': return tr('Ondas', 'Waves', 'Ondes');
    case 'zebra': return tr('Cebra', 'Zebra', 'Zèbre');
    case 'camo': return tr('Camuflaje', 'Camouflage', 'Camouflage');
    case 'lightning': return tr('Relámpago', 'Lightning', 'Éclair');
    case 'scales': return tr('Escamas', 'Scales', 'Écailles');
    case 'mosaic': return tr('Mosaico', 'Mosaic', 'Mosaïque');
    case 'galaxy': return tr('Galaxia', 'Galaxy', 'Galaxie');
    case 'spain': return tr('España', 'Spain', 'Espagne');
    case 'portugal': return tr('Portugal', 'Portugal', 'Portugal');
    case 'france': return tr('Francia', 'France', 'France');
    case 'germany': return tr('Alemania', 'Germany', 'Allemagne');
    case 'italy': return tr('Italia', 'Italy', 'Italie');
    case 'uk': return tr('Reino Unido', 'United Kingdom', 'Royaume-Uni');
    case 'usa': return tr('Estados Unidos', 'United States', 'États-Unis');
    case 'round-glasses': return tr('Gafas redondas', 'Round glasses', 'Lunettes rondes');
    case 'sunglasses': return tr('Gafas de sol', 'Sunglasses', 'Lunettes de soleil');
    case 'monocle': return tr('Monóculo', 'Monocle', 'Monocle');
    case 'mask': return tr('Máscara', 'Mask', 'Masque');
    case 'crown': return tr('Corona', 'Crown', 'Couronne');
    case 'tophat': return tr('Sombrero de copa', 'Top hat', 'Haut-de-forme');
    case 'scarf': return tr('Pañuelo pirata', 'Pirate bandana', 'Bandana de pirate');
    case 'halo': return tr('Aureola', 'Halo', 'Halo');
    case 'wings': return tr('Alas', 'Wings', 'Ailes');
    case 'eyepatch': return tr('Parche en el ojo', 'Eyepatch', 'Cache-œil');
    case 'basic': return tr('Básico', 'Basic', 'Basique');
    default:
      return capitalize(normalized.replace(/-/g, ' '));
  }
}

function formatType(type: CosmeticType, language: MenuLanguage): string {
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  switch (type) {
    case CosmeticType.BaseColor: return tr('Colores', 'Colors', 'Couleurs');
    case CosmeticType.Pattern: return tr('Patrones', 'Patterns', 'Motifs');
    case CosmeticType.Flag: return tr('Banderas', 'Flags', 'Drapeaux');
    case CosmeticType.Accessory: return tr('Accesorios', 'Accessories', 'Accessoires');
    case CosmeticType.Letter: return tr('Letras', 'Letters', 'Lettres');
    case CosmeticType.Badge: return tr('Insignias', 'Badges', 'Insignes');
    case CosmeticType.Nameplate: return tr('Nameplates', 'Nameplates', 'Plaques');
    case CosmeticType.VictoryEffect: return tr('Victoria', 'Victory', 'Victoire');
    case CosmeticType.Skin: return tr('Skins', 'Skins', 'Skins');
    case CosmeticType.Trail: return tr('Estelas', 'Trails', 'Traînées');
  }
}

function formatRarity(rarity: CosmeticRarity, language: MenuLanguage): string {
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  switch (rarity) {
    case CosmeticRarity.Common: return tr('Común', 'Common', 'Commun');
    case CosmeticRarity.Rare: return tr('Raro', 'Rare', 'Rare');
    case CosmeticRarity.Epic: return tr('Épico', 'Epic', 'Épique');
    case CosmeticRarity.Legendary: return tr('Legendario', 'Legendary', 'Légendaire');
    case CosmeticRarity.Special: return tr('Especial', 'Special', 'Spécial');
  }
}

function renderState(
  item: CosmeticCatalogEntry,
  account: ReturnType<typeof useAccountController>,
  selectedSlot: CosmeticSlotKey | null,
  language: MenuLanguage,
): React.ReactNode {
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  if (selectedSlot && account.getEquippedCosmeticId(selectedSlot) === item.id) return tr('Equipado', 'Equipped', 'Équipé');
  if (account.isOwned(item.id)) return tr('Disponible', 'Available', 'Disponible');
  if (item.priceSoftCurrency != null && item.priceSoftCurrency > 0) {
    return (
      <span className="details-price">
        <CoinIcon size={10} />
        {item.priceSoftCurrency}
      </span>
    );
  }
  if (item.isDefault) return tr('Gratis', 'Free', 'Gratuit');
  return tr('Bloqueado', 'Locked', 'Bloqué');
}

function buildPreviewStyle(item: CosmeticCatalogEntry): React.CSSProperties {
  if (item.type === CosmeticType.BaseColor) {
    return { background: resolveBaseColor(item.id) };
  }
  if (item.type === CosmeticType.Pattern) {
    return { background: resolvePatternPreview(item.id) };
  }
  if (item.type === CosmeticType.Flag) {
    return { background: resolveFlagGradient(item.id) };
  }
  if (item.type === CosmeticType.Accessory) {
    return { background: resolveBaseColor('cosm_base_blue') };
  }
  return { background: 'rgba(28,28,62,.92)' };
}

function renderCardPreview(item: CosmeticCatalogEntry, accessoryColor: string | null): React.ReactNode {
  const assetPath = getCardAssetPath(item);
  if (assetPath) {
    if (item.type === CosmeticType.Accessory) {
      const tint = accessoryColor ?? '#ffffff';
      return (
        <>
          <div className="cosmetic-card-preview-fill" style={buildPreviewStyle(item)} />
          <span
            className="cosmetic-card-accessory-tint"
            aria-hidden="true"
            style={{
              backgroundColor: tint,
              WebkitMaskImage: `url("${assetPath}")`,
              mask: `url("${assetPath}") no-repeat center / contain`,
              WebkitMask: `url("${assetPath}") no-repeat center / contain`,
            }}
          />
        </>
      );
    }
    return (
      <>
        <img className={`cosmetic-card-preview-image cosmetic-card-preview-image-${item.type}`} src={assetPath} alt="" />
      </>
    );
  }
  return <div className="cosmetic-card-preview-fill" style={buildPreviewStyle(item)} />;
}

function getCardAssetPath(item: CosmeticCatalogEntry): string | null {
  switch (item.type) {
    case CosmeticType.Flag:
      return `/assets/cosmetics/flags/${item.slug.replace('flag-', '')}.png`;
    case CosmeticType.Pattern:
      return `/assets/cosmetics/patterns/${item.slug.replace('pattern-', '')}.png`;
    case CosmeticType.Accessory:
      if (item.id === 'cosm_acc_eyepatch') {
        return '/assets/cosmetics/accessories/headband.png';
      }
      return `/assets/cosmetics/accessories/${item.slug.replace('accessory-', '')}.png`;
    default:
      return null;
  }
}

function resolveBaseColor(id: string): string {
  if (id.includes('red')) return 'linear-gradient(180deg, #ff6b7a, #d13f58)';
  if (id.includes('green')) return 'linear-gradient(180deg, #4ce79c, #1fa868)';
  if (id.includes('yellow')) return 'linear-gradient(180deg, #ffd85c, #d8a632)';
  if (id.includes('purple')) return 'linear-gradient(180deg, #b77dff, #7b46e5)';
  if (id.includes('white')) return 'linear-gradient(180deg, #f8f8ff, #c6d0f0)';
  if (id.includes('orange')) return 'linear-gradient(180deg, #ffa54b, #e07a2a)';
  if (id.includes('pink')) return 'linear-gradient(180deg, #ff7eb3, #e0508a)';
  if (id.includes('cyan')) return 'linear-gradient(180deg, #5ce1e6, #2aa8b3)';
  if (id.includes('lime')) return 'linear-gradient(180deg, #9bff5c, #5fbf2a)';
  if (id.includes('coral')) return 'linear-gradient(180deg, #ff7f6b, #d4553f)';
  if (id.includes('teal')) return 'linear-gradient(180deg, #4cd4b0, #1fa882)';
  if (id.includes('indigo')) return 'linear-gradient(180deg, #7b7dff, #4646c9)';
  if (id.includes('violet')) return 'linear-gradient(180deg, #d47dff, #a046d4)';
  if (id.includes('gold')) return 'linear-gradient(180deg, #ffd966, #d4a83a)';
  if (id.includes('silver')) return 'linear-gradient(180deg, #d8d8e0, #a0a0b8)';
  return 'linear-gradient(180deg, #5b9dff, #3d6fd3)';
}

function resolvePatternPreview(id: string): string {
  if (id.includes('rings')) return 'radial-gradient(circle, transparent 0 20%, rgba(255,255,255,.28) 20% 26%, transparent 26% 46%, rgba(255,255,255,.18) 46% 52%, transparent 52%), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('dots')) return 'radial-gradient(circle at 30% 30%, rgba(255,255,255,.24) 0 9%, transparent 10%), radial-gradient(circle at 70% 65%, rgba(255,255,255,.18) 0 8%, transparent 9%), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('chevron')) return 'repeating-radial-gradient(ellipse at 50% 100%, transparent 0 10px, rgba(255,255,255,.22) 11px 13px, transparent 14px 24px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('zebra')) return 'repeating-linear-gradient(90deg, transparent 0 18px, rgba(255,255,255,.20) 18px 22px, transparent 22px 40px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('camo')) return 'radial-gradient(ellipse at 20% 25%, rgba(255,255,255,.15) 0 15px, transparent 16px), radial-gradient(ellipse at 70% 60%, rgba(255,255,255,.12) 0 12px, transparent 13px), radial-gradient(ellipse at 45% 80%, rgba(255,255,255,.10) 0 10px, transparent 11px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('lightning')) return 'repeating-linear-gradient(135deg, rgba(255,255,255,.26) 0 3px, transparent 3px 11px, rgba(255,255,255,.12) 11px 14px, transparent 14px 22px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('scale')) return 'repeating-linear-gradient(0deg, transparent 0 14px, rgba(255,255,255,.18) 14px 16px, transparent 16px 30px), repeating-linear-gradient(90deg, transparent 0 14px, rgba(255,255,255,.18) 14px 16px, transparent 16px 30px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('mosaic')) return 'repeating-conic-gradient(rgba(255,255,255,.18) 0 25%, transparent 0 50%) 0 0 / 28px 28px, linear-gradient(180deg, #5b9dff, #3d6fd3)';
  if (id.includes('galaxy')) return 'radial-gradient(circle at 30% 40%, rgba(255,255,255,.28) 0 6px, transparent 8px), radial-gradient(circle at 70% 30%, rgba(255,255,255,.15) 0 4px, transparent 6px), radial-gradient(circle at 50% 70%, rgba(255,255,255,.20) 0 5px, transparent 7px), radial-gradient(circle at 20% 80%, rgba(255,255,255,.10) 0 3px, transparent 5px), linear-gradient(180deg, #1a1040, #2d1a6e)';
  return 'repeating-linear-gradient(135deg, rgba(255,255,255,.25) 0 7px, transparent 7px 14px), linear-gradient(180deg, #5b9dff, #3d6fd3)';
}

function resolveFlagGradient(id: string): string {
  if (id.includes('spain')) return 'linear-gradient(180deg, #c7222a 0 28%, #f7d54a 28% 72%, #c7222a 72% 100%)';
  if (id.includes('portugal')) return 'linear-gradient(90deg, #1f8b4c 0 40%, #c93232 40% 100%)';
  if (id.includes('france')) return 'linear-gradient(90deg, #2749b9 0 33%, #f7f7f7 33% 66%, #d43d3d 66% 100%)';
  if (id.includes('germany')) return 'linear-gradient(180deg, #111 0 33%, #d12d2d 33% 66%, #efbf42 66% 100%)';
  if (id.includes('italy')) return 'linear-gradient(90deg, #1f9c53 0 33%, #f7f7f7 33% 66%, #d43d3d 66% 100%)';
  if (id.includes('uk')) return 'linear-gradient(180deg, #243f99, #324fb3)';
  return 'linear-gradient(180deg, #b22234 0 14%, #fff 14% 28%, #b22234 28% 42%, #fff 42% 56%, #b22234 56% 70%, #fff 70% 84%, #3c3b90 84% 100%)';
}
